import { BrowserRouter } from "react-router-dom";
import { AppRoutes } from "./router";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { useState, useEffect, type ReactNode, Component, type ErrorInfo } from "react";

function ErrorFallback({ error }: { error: Error }) {
  return (
    <div className="flex items-center justify-center min-h-screen bg-white p-8">
      <div className="max-w-lg w-full">
        <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center mb-6">
          <span className="text-red-600 text-xl font-bold">!</span>
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h1>
        <p className="text-sm text-gray-500 mb-4">
          The app failed to load. This is usually a configuration issue.
        </p>
        <div className="bg-gray-50 rounded-lg p-4 overflow-auto">
          <pre className="text-xs font-mono text-red-700 whitespace-pre-wrap">{error.message}</pre>
          {error.stack && (
            <pre className="text-[10px] font-mono text-gray-500 mt-2 whitespace-pre-wrap">{error.stack}</pre>
          )}
        </div>
      </div>
    </div>
  );
}

class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo) {
    // Log to console for debugging
    console.error("App ErrorBoundary caught:", _error, _info);
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return <ErrorFallback error={this.state.error} />;
    }
    return this.props.children;
  }
}

// Safe wrapper that catches initialization errors
function SafeApp() {
  const [initError, setInitError] = useState<Error | null>(null);

  useEffect(() => {
    try {
      // Trigger any lazy imports or initialization that might throw
      // The error boundary will catch render-time errors
    } catch (err) {
      if (err instanceof Error) setInitError(err);
    }
  }, []);

  if (initError) {
    return <ErrorFallback error={initError} />;
  }

  return (
    <I18nextProvider i18n={i18n}>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter basename={__BASE_PATH__}>
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </I18nextProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <SafeApp />
    </ErrorBoundary>
  );
}

export default App;
